"use client";

import { Translate } from "@/components/Translate";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableHeader,
	TableRow,
	TableScrollContainer,
} from "@/components/ui/Table";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { useCallback } from "react";

import styles from "../BuyBox/BuyBox.module.css";
import { SizeSelector, type GetSizeProps } from "../BuyBox/BuyBox";
import type { ProductDetails } from "@/lib/centra";
import { clsx } from "@frend-digital/ui";
import { useAtomValue } from "jotai";
import { sizeChartAtom } from "../BuyBox/sizeChartState";

interface SizeChartTableProps {
	tableData: string[][];
	sizeHeaders: string[];
	itemTable?: ProductDetails["itemTable"];
	sizes: ProductDetails["items"];
	getSizeProps: GetSizeProps;
	kidsShoesDesc?: string;
	isKidsShoes?: boolean;
	sizesOnXAxis: boolean;
}

export const SizeChartTable = ({
	tableData,
	sizeHeaders,
	itemTable,
	isKidsShoes,
	kidsShoesDesc,
	sizes,
	getSizeProps,
	sizesOnXAxis,
}: SizeChartTableProps) => {
	const searchParams = useUrlSearchParams();
	const open = useAtomValue(sizeChartAtom);
	const selectedSize = searchParams.get("size");

	const handleRef = useCallback((node: HTMLTableCellElement | null) => {
		if (node) {
			setTimeout(() => {
				node.scrollIntoView({
					behavior: "smooth",
					block: "nearest",
					inline: "center",
				});
			}, 15);
		}
	}, []);

	// Helper function to match size labels (e.g., "19" matches "EU 19")
	const matchesSize = (label: string, size: string): boolean => {
		if (label === size) return true;
		if (label.endsWith(size) || label.endsWith(` ${size}`)) return true;
		const withoutPrefix = label.replace(/^[A-Z]+\s+/, "");
		return withoutPrefix === size;
	};

	const measurementTypes = tableData.slice(1).map((row) => row[0]);

	return (
		<>
			<div>
				<h3 className={styles.title}>
					<Translate>Sizes</Translate>
				</h3>
				<SizeSelector sizes={sizes} getSizeProps={getSizeProps} />
			</div>
			<div>
				<div className={styles.titleWrapper}>
					<h3 className={styles.title}>
						<Translate>Measurements</Translate>
					</h3>
					{isKidsShoes && kidsShoesDesc && (
						<span className={styles.kidsDesc}> {kidsShoesDesc}</span>
					)}
				</div>
				<TableContainer>
					<TableScrollContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableHeader className={clsx(styles.sticky)}>
										<Translate>Size</Translate>
									</TableHeader>

									{sizeHeaders.map((sizeHeader, index) => {
										const isSelected = sizesOnXAxis && selectedSize && matchesSize(sizeHeader, selectedSize);
										return (
											<TableHeader
												key={`size-${index}-${sizeHeader}`}
												className={isSelected ? styles.selectedColumn : ""}
												ref={open && isSelected ? handleRef : null}
											>
												<Translate>{sizeHeader}</Translate>
											</TableHeader>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody className="size-chart-body">
								{measurementTypes.map((measurement, measIndex) => {
									const isRowSelected = !sizesOnXAxis && selectedSize && matchesSize(measurement, selectedSize);
									return (
										<TableRow
											key={`measurement-row-${measIndex}-${measurement}`}
										>
											<TableHeader
												className={clsx(styles.sticky)}
												ref={open && isRowSelected ? handleRef : null}
											>
												<Translate>{measurement}</Translate>
											</TableHeader>

											{sizeHeaders.map((sizeHeader, sizeIndex) => {
												const value = tableData[measIndex + 1][sizeIndex + 1];
												const isColSelected = sizesOnXAxis && selectedSize && matchesSize(sizeHeader, selectedSize);
												const isSelected = isColSelected || isRowSelected;

												return (
													<TableCell
														key={`${measurement}-${sizeHeader}`}
														className={isSelected ? styles.selectedColumn : ""}
													>
														{value}
													</TableCell>
												);
											})}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableScrollContainer>
				</TableContainer>
			</div>
		</>
	);
};
